/**
 * PostHog client (browser).
 *
 * Initialized once at app startup via `initPostHog()`. Disabled when
 * `VITE_POSTHOG_KEY` is empty/unset OR `import.meta.env.PROD === false`,
 * so events from a developer's laptop don't leak into production data.
 *
 * Replaces both Mixpanel (analytics) and Sentry (frontend errors): PostHog
 * supports `capture()` for product events and `captureException()` for
 * runtime errors against the same project + distinct id.
 */

import posthog from "posthog-js";

const KEY = (import.meta.env.VITE_POSTHOG_KEY as string | undefined)?.trim();

const DEFAULT_API_HOST = "https://us.i.posthog.com";
const DEFAULT_UI_HOST = "https://us.posthog.com";

/**
 * PostHog Cloud's own hosts, for which posthog-js derives the app host
 * itself by rewriting `.i.posthog.com` to `.posthog.com` (and
 * `app.posthog.com` to `us.posthog.com`).
 */
const POSTHOG_CLOUD_HOST = /^https:\/\/(app|us|eu)(\.i)?\.posthog\.com\/?$/i;

export interface PostHogHosts {
	apiHost: string;
	/** Undefined leaves `ui_host` unset, so posthog-js derives it. */
	uiHost?: string;
}

/**
 * Resolve `api_host` and `ui_host` from the build env.
 *
 * Behind PostHog's managed reverse proxy, `api_host` is our own CNAME
 * subdomain. posthog-js would derive the app host from it unchanged, so
 * the toolbar and "view in PostHog" links would point at the proxy. A
 * subdomain does not say which region the project is in, so the app host
 * comes from `VITE_POSTHOG_UI_HOST`, defaulting to US — the region the
 * backend's `POSTHOG_HOST` defaults to.
 */
export function resolvePostHogHosts(env: {
	host?: string;
	uiHost?: string;
}): PostHogHosts {
	const apiHost = env.host?.trim() || DEFAULT_API_HOST;
	const explicitUiHost = env.uiHost?.trim();
	if (explicitUiHost) return { apiHost, uiHost: explicitUiHost };
	if (POSTHOG_CLOUD_HOST.test(apiHost)) return { apiHost };
	return { apiHost, uiHost: DEFAULT_UI_HOST };
}

const HOSTS = resolvePostHogHosts({
	host: import.meta.env.VITE_POSTHOG_HOST as string | undefined,
	uiHost: import.meta.env.VITE_POSTHOG_UI_HOST as string | undefined,
});

let initialized = false;

function isEnabled(): boolean {
	if (!KEY) return false;
	// `PROD` is true only in `vite build` output; `vite dev` is `false`.
	return import.meta.env.PROD === true;
}

/**
 * Initialize PostHog. Safe to call multiple times — only the first call
 * does any work. No-op when disabled.
 */
export function initPostHog(): void {
	if (initialized) return;
	initialized = true;

	if (!isEnabled()) {
		return;
	}

	// `main.tsx` calls this BEFORE `createRoot().render()`, so a throw out
	// of `init()` is a blank page caused by analytics. `initialized` is
	// already set above, so a failed init stays failed rather than being
	// retried on every call.
	try {
		posthogInit();
	} catch {
		// fail silent — the app renders without analytics
	}
}

function posthogInit(): void {
	posthog.init(KEY as string, {
		api_host: HOSTS.apiHost,
		...(HOSTS.uiHost ? { ui_host: HOSTS.uiHost } : {}),
		// Capture pageviews + clicks + form submits without per-route hooks.
		capture_pageview: true,
		capture_pageleave: true,
		autocapture: true,
		// Never start recording at init. `lib/session-replay` turns it on
		// once it knows whether the visitor is authenticated, opted out and
		// in the sample — none of which is known this early. A dashboard
		// switch alone cannot start it, because every gate is client-side.
		disable_session_recording: true,
		// rrweb reads this when recording STARTS, so it has to be set here
		// even though nothing records yet; a later change would not reach a
		// session already underway.
		session_recording: {
			// Masks the VALUE of every input rrweb knows how to mask —
			// which includes `password` and `tel`, issue #147's floor.
			// Stricter than that floor deliberately: one decision instead
			// of one per field, over inputs carrying addresses and budgets.
			//
			// This is already rrweb's default, but setting it explicitly
			// also pins it: posthog-js resolves masking as `client ??
			// server`, so a `masking` remote config on the project cannot
			// relax it from the dashboard.
			maskAllInputs: true,
			// Non-input content marked sensitive, and its descendants —
			// rrweb resolves this with `closest()`. Text nodes only: it
			// does not mask attributes or image sources. Masking preserves
			// length and whitespace, so the layout still reads as the page.
			//
			// Input VALUES do not go through this selector at all; they go
			// through `maskAllInputs` above. Which is why that is on.
			maskTextSelector: "[data-sensitive]",
		},
		// Don't generate person profiles for anonymous traffic. Identify on
		// OTP-verify is the only path that promotes a session to a person.
		person_profiles: "identified_only",
	});
}

/**
 * The live client, or `null` when PostHog is disabled or `initPostHog()`
 * has not run yet. Exposed for `lib/feature-flags` and
 * `lib/session-replay`, which need the instance itself rather than one of
 * the wrappers below; application code should not reach for it.
 */
export function getPostHogClient(): typeof posthog | null {
	if (!initialized || !isEnabled()) return null;
	return posthog;
}

/**
 * Bind subsequent events to a known user. Call from auth.store after a
 * successful login / OTP verify. The trait shape is intentionally open
 * (`Record<string, unknown>`); the typed event catalog lives in
 * `services/analytics.service.ts`.
 */
export function identifyUser(
	userId: string,
	traits?: Record<string, unknown>,
): void {
	if (!isEnabled()) return;
	try {
		posthog.identify(userId, traits);
	} catch {
		// fail silent — analytics must never break the app
	}
}

/**
 * Update user properties without re-identifying. Useful for profile-update
 * flows where we want the latest profile flags on the existing distinct id.
 */
export function setUserProperties(traits: Record<string, unknown>): void {
	if (!isEnabled()) return;
	try {
		posthog.setPersonProperties(traits);
	} catch {
		// fail silent
	}
}

/**
 * Fire-and-forget custom event capture. Property keys should follow the
 * catalog in `analytics.service.ts`.
 */
export function track(
	event: string,
	properties?: Record<string, unknown>,
): void {
	if (!isEnabled()) return;
	try {
		posthog.capture(event, properties);
	} catch {
		// fail silent
	}
}

/** Clear identity on logout so subsequent events are anonymous. */
export function resetUser(): void {
	if (!isEnabled()) return;
	try {
		posthog.reset();
	} catch {
		// fail silent
	}
}

/**
 * Send an exception to PostHog's error tracking. Use this from
 * ErrorBoundary and any high-level catch sites that don't already throw
 * to a PromiseRejection handler.
 */
export function captureException(
	error: unknown,
	additionalProperties?: Record<string, unknown>,
): void {
	if (!isEnabled()) return;
	try {
		const err = error instanceof Error ? error : new Error(String(error));
		posthog.captureException(err, additionalProperties);
	} catch {
		// fail silent
	}
}
